import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import clsx from 'clsx';
import { Layers } from 'lucide-react';
import type { Bucket, MediaItem } from '../../types';
import { useThumbnail } from '../hooks/useImageUrl';

const TILE = 82;
const GAP = 4;
const STRIDE = TILE + GAP;
const OVERSCAN = 8;

/**
 * The filmstrip only renders the tiles near the viewport.
 *
 * A wedding card runs to several thousand frames; mounting a tile for each one
 * would cost more than the culling loop itself, so the strip keeps a window of
 * real elements between two spacers.
 */
export default function Filmstrip({
  items,
  index,
  buckets,
  markers,
  depth,
  onPick,
}: {
  items: MediaItem[];
  index: number;
  buckets: Bucket[];
  /** Review Assist markers, keyed by media id. Empty until a shoot is reviewed. */
  markers?: Map<number, { isSoft: boolean; isPick: boolean }>;
  /** How many frames sit behind a collapsed stack, keyed by the frame on top. */
  depth?: Map<number, number>;
  onPick: (index: number) => void;
}) {
  const scroller = useRef<HTMLDivElement>(null);
  const [range, setRange] = useState({ start: 0, end: 40 });

  const measure = () => {
    const element = scroller.current;
    if (!element) return;

    const first = Math.floor(element.scrollLeft / STRIDE);
    const visible = Math.ceil(element.clientWidth / STRIDE);
    setRange({
      start: Math.max(0, first - OVERSCAN),
      end: Math.min(items.length, first + visible + OVERSCAN),
    });
  };

  useLayoutEffect(measure, [items.length]);

  // Keep the current frame in view as the photographer moves through.
  useEffect(() => {
    const element = scroller.current;
    if (!element) return;

    const left = index * STRIDE;
    const right = left + TILE;
    const viewLeft = element.scrollLeft;
    const viewRight = viewLeft + element.clientWidth;

    if (left < viewLeft + STRIDE || right > viewRight - STRIDE) {
      element.scrollTo({
        left: Math.max(0, left - element.clientWidth / 2 + TILE / 2),
        behavior: 'smooth',
      });
    }
  }, [index]);

  const colourOf = (item: MediaItem) => {
    const bucket = buckets.find((b) => b.id === item.bucketId);
    if (!bucket) return null;
    return bucket.kind === 'keep' ? 'bg-keep' : 'bg-reject';
  };

  return (
    <div
      ref={scroller}
      onScroll={measure}
      className="flex h-[104px] items-center gap-1 overflow-x-auto overflow-y-hidden border-t border-ink-850 bg-ink-900 px-3"
      role="listbox"
      aria-label="Filmstrip"
    >
      <div style={{ width: range.start * STRIDE }} className="shrink-0" aria-hidden="true" />

      {items.slice(range.start, range.end).map((item, offset) => {
        const position = range.start + offset;
        const isCurrent = position === index;
        return (
          <Tile
            key={item.id}
            item={item}
            position={position}
            isCurrent={isCurrent}
            colour={colourOf(item)}
            soft={markers?.get(item.id)?.isSoft ?? false}
            pick={markers?.get(item.id)?.isPick ?? false}
            behind={depth?.get(item.id) ?? 0}
            onPick={onPick}
          />
        );
      })}

      <div
        style={{ width: Math.max(0, (items.length - range.end) * STRIDE) }}
        className="shrink-0"
        aria-hidden="true"
      />
    </div>
  );
}

function Tile({
  item,
  position,
  isCurrent,
  colour,
  soft,
  pick,
  behind,
  onPick,
}: {
  item: MediaItem;
  position: number;
  isCurrent: boolean;
  soft: boolean;
  pick: boolean;
  /** Frames hidden behind this one. 0 when it stands alone. */
  behind: number;
  colour: string | null;
  onPick: (index: number) => void;
}) {
  const url = useThumbnail(item);
  const stacked = behind > 1;

  return (
    <div className="relative shrink-0">
      {/* One sliver of paper behind the tile, sized to the 4px gap so it never
          crowds its neighbour. The badge below carries the actual count; this
          just says at a glance that there is something behind. */}
      {stacked && (
        <span
          aria-hidden="true"
          className="absolute -right-[3px] top-[4px] h-[74px] w-[82px] rounded bg-ink-750"
        />
      )}
      <button
      onClick={() => onPick(position)}
      role="option"
      aria-selected={isCurrent}
      aria-label={stacked ? `${item.filename}, ${behind} frames in this burst` : item.filename}
      className={clsx(
        'relative block h-[82px] w-[82px] shrink-0 overflow-hidden rounded transition',
        isCurrent ? 'ring-2 ring-ink-50' : 'opacity-70 hover:opacity-100',
      )}
    >
      {url ? (
        <img
          src={url}
          alt=""
          loading="lazy"
          className="h-full w-full object-cover"
          style={item.rotation ? { transform: `rotate(${item.rotation}deg)` } : undefined}
        />
      ) : (
        <div className="h-full w-full animate-pulse2 bg-ink-800" />
      )}

      {colour && <span className={clsx('absolute inset-x-0 bottom-0 h-[3px]', colour)} />}

      {/* Amber, not red: a hint to look closer, never a verdict. */}
      {soft && (
        <span
          title="Looks blurry, worth a closer look"
          className="absolute left-1 top-1 h-2 w-2 rounded-full bg-warn ring-2 ring-ink-950/60"
        />
      )}

      {/* Teal, apart from keep and reject as well as from soft: a suggestion
          about which frame to look at, not a decision about any of them.
          A pill rather than a circle because it has to carry two letters. */}
      {pick && (
        <span
          title="Suggested by Review Assist: the sharpest of this burst"
          className="absolute right-1 top-1 flex h-4 items-center rounded-full bg-pick px-1.5 text-[9px] font-bold tracking-tight text-ink-950 ring-2 ring-ink-950/60"
        >
          A.I.
        </span>
      )}

      {stacked && (
        <span className="tnum absolute bottom-1 right-1 flex items-center gap-0.5 rounded bg-ink-950/85 px-1 py-px text-[10px] font-medium text-ink-100">
          <Layers size={9} />
          {behind}
        </span>
      )}

      {item.status !== 'ok' && (
        <span className="absolute inset-0 flex items-center justify-center bg-ink-950/70 text-[10px] text-reject">
          missing
        </span>
      )}
      </button>
    </div>
  );
}
